import React, { useEffect, useState } from "react";
import { Edit, Plus, RotateCw, Save, X } from "lucide-react";
import { fetchNews, createNews, updateNews, fetchActiveNetworks } from "../services/news";
import AdminCardActions from "../components/AdminCardActions";
import FilterPopover, { DateRangeFields } from "../components/FilterPopover";
import ToolbarSearch from "../components/ToolbarSearch";

const NewsAdmins = () => {
    const [news, setNews] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredNews, setFilteredNews] = useState([]);
    const [inactiveFrom, setInactiveFrom] = useState("");
    const [inactiveTo, setInactiveTo] = useState("");
    const [sortBy, setSortBy] = useState("title");
    const [sortDir, setSortDir] = useState("asc");
    const [networks, setNetworks] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [expandedMobileId, setExpandedMobileId] = useState(null);
    const [expandedDesktopId, setExpandedDesktopId] = useState(null);
    const [editedData, setEditedData] = useState({
        title: "",
        content: "",
        status: true,
        network_name: "",
    });
    const [originalData, setOriginalData] = useState({});
    const [newNews, setNewNews] = useState({
        title: "",
        content: "",
        status: true,
        network_name: "",
    });
    const [showAddModal, setShowAddModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [loading, setLoading] = useState(false);

    const normalizeText = (text) => (typeof text === "string" ? text : "");

    const getPreviewLine = (text) => {
        const value = normalizeText(text);
        const normalized = value.replace(/\r\n/g, "\n");
        const idx = normalized.indexOf("\n");
        return idx === -1 ? normalized : normalized.slice(0, idx);
    };

    const hasMoreContent = (text) => {
        const value = normalizeText(text);
        const normalized = value.replace(/\r\n/g, "\n");
        const preview = getPreviewLine(normalized);
        return normalized.includes("\n") || normalized.length > preview.length;
    };

    const inactiveFilterEnabled = Boolean(inactiveFrom || inactiveTo);

    useEffect(() => {
        loadData();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [newsData, networksData] = await Promise.all([
                fetchNews(),
                fetchActiveNetworks(),
            ]);
            setNews(newsData);
            setNetworks(networksData.filter((n) => n.status));
        } catch (err) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    const parseDate = (dateStr, endOfDay = false) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return null;
        if (endOfDay) d.setHours(23, 59, 59, 999);
        else d.setHours(0, 0, 0, 0);
        return d;
    };

    useEffect(() => {
        const query = searchQuery.toLowerCase();
        const fromDate = parseDate(inactiveFrom, false);
        const toDate = parseDate(inactiveTo, true);
        const dateFilterOn = Boolean(inactiveFrom || inactiveTo);

        const filtered = news.filter((item) => {
            const matchesQuery =
                item.title.toLowerCase().includes(query) ||
                item.content.toLowerCase().includes(query);
            if (!matchesQuery) return false;

            const isActive = Boolean(item.status);
            if (isActive) return true;
            if (!dateFilterOn) return false;

            if (!item.dtlm) return false;
            const itemDate = new Date(item.dtlm);
            if (Number.isNaN(itemDate.getTime())) return false;
            if (fromDate && itemDate < fromDate) return false;
            if (toDate && itemDate > toDate) return false;
            return true;
        });
        setFilteredNews(filtered);
    }, [news, searchQuery, inactiveFrom, inactiveTo]);

    const handleEdit = (item) => {
        setEditingId(item.id);
        setExpandedDesktopId(null);
        setExpandedMobileId(item.id);
        setEditedData({
            title: item.title,
            content: item.content,
            status: item.status,
            network_name: item.network_name || "",
        });
        setOriginalData({
            title: item.title,
            content: item.content,
            status: item.status,
            network_name: item.network_name || "",
        });
    };

    const handleCancelEdit = () => {
        setEditedData(originalData);
        setEditingId(null);
    };

    const toggleDesktopExpand = (id) => {
        setExpandedDesktopId((prev) => (prev === id ? null : id));
    };

    const toggleMobileExpand = (id) => {
        setExpandedMobileId((prev) => (prev === id ? null : id));
    };

    const handleSave = async (id) => {
        try {
            await updateNews(id, editedData);
            setNews(news.map((x) => (x.id === id ? { ...x, ...editedData } : x)));
            setEditingId(null);
        } catch (err) {
            alert(err.message);
        }
    };

    const handleAdd = async () => {
        if (!newNews.title.trim() || !newNews.content.trim()) {
            alert("Заголовок та контент не можуть бути порожніми!");
            return;
        }
        try {
            await createNews(newNews);
            setNewNews({ title: "", content: "", status: true, network_name: "" });
            setShowAddModal(false);
            loadData();
        } catch (err) {
            alert(err.message);
        }
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredNews) return [];
        const sorted = [...filteredNews];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            }
            return valA < valB ? 1 : valA > valB ? -1 : 0;
        });
        return sorted;
    };

    const clearDateFilter = () => {
        setInactiveFrom("");
        setInactiveTo("");
    };

    return (
        <div className="news-popup news-admin-page">
            <div className="config-header">
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowAddModal(true)} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadData} disabled={loading} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                    </button>
                    <FilterPopover
                        active={inactiveFilterEnabled}
                        title="Неактивні за період"
                        hint="Активні новини завжди видно. Період показує неактивні за датою зміни."
                        buttonTitle="Фільтр неактивних за датою"
                        onClear={clearDateFilter}
                    >
                        <DateRangeFields
                            from={inactiveFrom}
                            to={inactiveTo}
                            onFromChange={setInactiveFrom}
                            onToChange={setInactiveTo}
                        />
                    </FilterPopover>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук за заголовком чи контентом..."
                    />
                </div>
            </div>

            {showAddModal && (
                <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Додати новину</h3>

                        <input
                            type="text"
                            placeholder="Заголовок"
                            value={newNews.title}
                            onChange={(e) => setNewNews({ ...newNews, title: e.target.value })}
                        />

                        <textarea
                            placeholder="Контент"
                            value={newNews.content}
                            onChange={(e) => setNewNews({ ...newNews, content: e.target.value })}
                            rows={4}
                        />

                        <select
                            name="network_name"
                            value={newNews.network_name}
                            onChange={(e) => setNewNews({ ...newNews, network_name: e.target.value })}
                        >
                            <option value="">Оберіть мережу</option>
                            {networks.map((n) => (
                                <option key={n.id} value={n.network_name}>
                                    {n.network_name}
                                </option>
                            ))}
                        </select>

                        <select
                            name="status"
                            value={newNews.status}
                            onChange={(e) => setNewNews({ ...newNews, status: e.target.value === "true" })}
                        >
                            <option value="true">Активна</option>
                            <option value="false">Неактивна</option>
                        </select>

                        <div className="modal-actions">
                            <button className="add-btn" onClick={handleAdd}>Додати</button>
                            <button onClick={() => setShowAddModal(false)} className="add-btn cancel-btn">
                                Скасувати
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isMobile ? (
                <div className="config-grid news-admin-grid">
                    {filteredNews.map((item) => {
                        const expanded = editingId === item.id || expandedMobileId === item.id;
                        return (
                            <div
                                key={item.id}
                                className={`config-card news-admin-card ${expanded ? "is-expanded" : ""} ${editingId === item.id ? "is-editing" : ""}`}
                                onClick={() => {
                                    if (editingId === item.id) return;
                                    toggleMobileExpand(item.id);
                                }}
                                onKeyDown={(e) => {
                                    if (editingId === item.id) return;
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleMobileExpand(item.id);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                aria-expanded={expanded}
                            >
                                <div className="news-admin-card__top">
                                    <div className="news-admin-card__main">
                                        {editingId === item.id ? (
                                            <input
                                                type="text"
                                                value={editedData.title}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) =>
                                                    setEditedData({ ...editedData, title: e.target.value })
                                                }
                                            />
                                        ) : (
                                            <div className="news-admin-card__title-row">
                                                <h3 className="news-admin-card__title">{item.title}</h3>
                                                <span className="news-admin-card__chevron" aria-hidden="true">
                                                    {expanded ? "▲" : "▼"}
                                                </span>
                                            </div>
                                        )}
                                        <div className="news-admin-card__meta">
                                            <span>{new Date(item.dtlm || Date.now()).toLocaleString("uk-UA")}</span>
                                            <span className={`news-admin-card__status ${item.status ? "is-active" : "is-inactive"}`}>
                                                {item.status ? "Активна" : "Неактивна"}
                                            </span>
                                        </div>
                                        {!expanded ? (
                                            <p className="news-admin-card__preview">{getPreviewLine(item.content)}</p>
                                        ) : null}
                                    </div>

                                    <div onClick={(e) => e.stopPropagation()}>
                                        <AdminCardActions
                                            editing={editingId === item.id}
                                            onEdit={() => handleEdit(item)}
                                            onSave={() => handleSave(item.id)}
                                            onCancel={handleCancelEdit}
                                        />
                                    </div>
                                </div>

                                {expanded ? (
                                    <div className="news-admin-card__body">
                                        <div className="news-admin-card__field">
                                            {editingId === item.id ? (
                                                <textarea
                                                    value={editedData.content}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) =>
                                                        setEditedData({ ...editedData, content: e.target.value })
                                                    }
                                                />
                                            ) : (
                                                <div className="news-admin-card__content">{item.content}</div>
                                            )}
                                        </div>

                                        <div className="news-admin-card__field">
                                            <strong>Мережа</strong>
                                            {editingId === item.id ? (
                                                <select
                                                    value={editedData.network_name}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) =>
                                                        setEditedData({ ...editedData, network_name: e.target.value })
                                                    }
                                                >
                                                    <option value="">(Не прив'язано)</option>
                                                    {networks.map((n) => (
                                                        <option key={n.id} value={n.network_name}>
                                                            {n.network_name}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span>{item.network_name || "(Не прив'язано)"}</span>
                                            )}
                                        </div>

                                        <div className="news-admin-card__field">
                                            <strong>Статус</strong>
                                            {editingId === item.id ? (
                                                <select
                                                    value={editedData.status}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) =>
                                                        setEditedData({
                                                            ...editedData,
                                                            status: e.target.value === "true",
                                                        })
                                                    }
                                                >
                                                    <option value="true">Активна</option>
                                                    <option value="false">Неактивна</option>
                                                </select>
                                            ) : (
                                                <span>{item.status ? "Активна" : "Неактивна"}</span>
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="table-responsive">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("title")}>
                                    Заголовок {sortBy === "title" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("content")}>
                                    Контент {sortBy === "content" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("network_name")}>
                                    Мережа {sortBy === "network_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                                    Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("dtlm")}>
                                    Дата {sortBy === "dtlm" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((item) => (
                                <tr
                                    key={item.id}
                                    onClick={() => {
                                        if (editingId === item.id) return;
                                        toggleDesktopExpand(item.id);
                                    }}
                                    style={{ cursor: editingId === item.id ? "default" : "pointer" }}
                                >
                                    <td>
                                        {editingId === item.id ? (
                                            <input
                                                type="text"
                                                value={editedData.title}
                                                onChange={(e) =>
                                                    setEditedData({ ...editedData, title: e.target.value })
                                                }
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            item.title
                                        )}
                                    </td>
                                    <td>
                                        {editingId === item.id ? (
                                            <textarea
                                                value={editedData.content}
                                                onChange={(e) =>
                                                    setEditedData({ ...editedData, content: e.target.value })
                                                }
                                                style={{ width: "100%", minHeight: "40px", resize: "vertical" }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            expandedDesktopId === item.id ? (
                                                <div
                                                    className="news-admin-content expanded"
                                                    style={{ maxHeight: "180px", overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                                >
                                                    {item.content}
                                                </div>
                                            ) : (
                                                <div className="news-admin-content preview" title="Натисніть, щоб відкрити повністю">
                                                    <span className="news-admin-preview-text">{getPreviewLine(item.content)}</span>
                                                    {hasMoreContent(item.content) && (
                                                        <span className="news-admin-expand-indicator" aria-hidden="true">▼</span>
                                                    )}
                                                </div>
                                            )
                                        )}
                                    </td>
                                    <td>
                                        {editingId === item.id ? (
                                            <select
                                                value={editedData.network_name}
                                                onChange={(e) =>
                                                    setEditedData({ ...editedData, network_name: e.target.value })
                                                }
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <option value="">(Не прив'язано)</option>
                                                {networks.map((n) => (
                                                    <option key={n.id} value={n.network_name}>
                                                        {n.network_name}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            item.network_name || "(Не прив'язано)"
                                        )}
                                    </td>
                                    <td>
                                        {editingId === item.id ? (
                                            <select
                                                value={editedData.status}
                                                onChange={(e) =>
                                                    setEditedData({ ...editedData, status: e.target.value === "true" })
                                                }
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <option value="true">Активна</option>
                                                <option value="false">Неактивна</option>
                                            </select>
                                        ) : (
                                            item.status ? "✅" : "❌"
                                        )}
                                    </td>
                                    <td>{new Date(item.dtlm || Date.now()).toLocaleString("uk-UA")}</td>
                                    <td className="actions">
                                        {editingId === item.id ? (
                                            <>
                                                <button className="icon-button" onClick={(e) => { e.stopPropagation(); handleSave(item.id); }} title="Зберегти">
                                                    <Save size={14} />
                                                </button>
                                                <button className="icon-button" onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }} title="Скасувати">
                                                    <X size={14} />
                                                </button>
                                            </>
                                        ) : (
                                            <button className="icon-button" onClick={(e) => { e.stopPropagation(); handleEdit(item); }} title="Редагувати">
                                                <Edit size={14} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default NewsAdmins;
